import { useEffect, useState } from 'react';
import { admin } from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { Sparkline } from '@/components/Sparkline';
import { Pagination, usePaginated } from '@/components/Pagination';

type Summary = {
  byTime: {
    ostatnie24h: Record<string, number | string>;
    ostatnie7dni: Record<string, number | string>;
    ostatnie30dni: Record<string, number | string>;
    dzienneOstatnie30dni: { data: string; tokeny: number | string; costUsd: number | string }[];
  };
  perModel: Record<string, unknown>[];
  perStep: Record<string, unknown>[];
  perStudy: Record<string, unknown>[];
};

export function TokenUsage() {
  const [data, setData] = useState<Summary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    setErr(null);
    try {
      const s = await admin.tokenUsage.summary();
      setData(s as unknown as Summary);
    } catch (e: any) { setErr(e.message ?? String(e)); }
    finally { setLoading(false); }
  }

  async function exportCsv() {
    if (!data) return;
    const rows: string[] = ['section;study/model/krok;tokenyWe;tokenyWy;kosztUSD;kosztPLN;wywolan'];
    data.perStudy.forEach(p => {
      rows.push([
        'study',
        `"${p.number} - ${String(p.title).replace(/"/g, "'")}"`,
        p.inputTokens, p.outputTokens, p.costUsd, p.costPln, p.liczbaWywolan,
      ].join(';'));
    });
    data.perStep.forEach(p => {
      rows.push(['krok', p.code, p.inputTokens, p.outputTokens, p.costUsd, p.costPln, p.liczbaWywolan].join(';'));
    });
    data.perModel.forEach(p => {
      rows.push(['model', `${p.model} (${p.provider})`, p.inputTokens, p.outputTokens, p.costUsd, p.costPln, p.liczbaWywolan].join(';'));
    });
    const blob = new Blob(['\ufeff' + rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `token-usage-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Hooki Reacta MUSZĄ być wołane bezwarunkowo i w tej samej kolejności na każdym renderze —
  // dlatego usePaginated wołamy tutaj (przed early-return), z fallbackiem na pustą tablicę
  // gdy data jeszcze nie przyszło z backendu.
  const perModelPage = usePaginated(data?.perModel ?? [], { storageKey: 'token-per-model' });
  const perStepPage = usePaginated(data?.perStep ?? [], { storageKey: 'token-per-step' });
  const perStudyPage = usePaginated(data?.perStudy ?? [], { storageKey: 'token-per-study' });

  if (err) return <div className="text-red-500">Błąd: {err}</div>;
  if (loading || !data) return <div className="text-ink-400">Ładowanie statystyk…</div>;

  const d24 = data.byTime.ostatnie24h;
  const d7 = data.byTime.ostatnie7dni;
  const d30 = data.byTime.ostatnie30dni;

  const dailyTokens = data.byTime.dzienneOstatnie30dni.map(d => Number(d.tokeny));
  const dailyLabels = data.byTime.dzienneOstatnie30dni.map(d => String(d.data));
  const dailyCost = data.byTime.dzienneOstatnie30dni.map(d => Number(d.costUsd));

  return (
    <div className="max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Zużycie tokenów AI · oszacowane koszty</h1>
          <div className="text-ink-400 text-[13px]">
            Claude API / Ollama / mock — tokeny, koszty USD i PLN. Cennik w panelu konfiguracji.
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => void loadData()} className="btn btn-secondary btn-sm">
            <Icon name="arrow" className="w-3.5 h-3.5" /> Odśwież
          </button>
          <button onClick={exportCsv} className="btn btn-secondary btn-sm">
            <Icon name="download" className="w-3.5 h-3.5" /> Eksport CSV
          </button>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-3.5 mb-6">
        <KpiCard label="Ostatnie 24h" data={d24} color="bg-water-500" />
        <KpiCard label="Ostatnie 7 days" data={d7} color="bg-ai-500" />
        <KpiCard label="Ostatnie 30 days" data={d30} color="bg-amber-500" />
      </div>

      <div className="card mb-6">
        <div className="card-head">
          <div className="card-title">Dzienny trend zużycia (30 days)</div>
        </div>
        <div className="card-body">
          {dailyTokens.length === 0 ? (
            <div className="text-ink-400 text-[13px]">Brak danych — AI nie było jeszcze używane.</div>
          ) : (
            <>
              <div className="text-[12px] text-ink-500 mb-2">Tokeny dziennie:</div>
              <Sparkline data={dailyTokens} labels={dailyLabels} color="#7c5cff" height={80} />
              <div className="text-[12px] text-ink-500 mt-4 mb-2">Koszt dzienny (USD):</div>
              <Sparkline data={dailyCost} labels={dailyLabels} color="#10a97a" height={80} />
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5 mb-6">
        <div className="card">
          <div className="card-head"><div className="card-title">Per model</div></div>
          <div className="card-body">
            {data.perModel.length === 0 ? (
              <div className="text-ink-400 text-[13px]">Brak danych</div>
            ) : (
              <table className="table-hw">
                <thead>
                  <tr>
                    <th>Model / provider</th>
                    <th className="text-right">Wywołań</th>
                    <th className="text-right">Tokeny</th>
                    <th className="text-right">USD</th>
                    <th className="text-right">PLN</th>
                  </tr>
                </thead>
                <tbody>
                  {perModelPage.pagedItems.map((p, i) => (
                    <tr key={i}>
                      <td>
                        <div className="font-mono text-[12px]">{String(p.model)}</div>
                        <div className="text-[11px] text-ink-400">{String(p.provider)}</div>
                      </td>
                      <td className="text-right font-mono">{String(p.liczbaWywolan)}</td>
                      <td className="text-right font-mono">
                        {Number(p.inputTokens) + Number(p.outputTokens)}
                      </td>
                      <td className="text-right font-mono text-green-500">${Number(p.costUsd).toFixed(4)}</td>
                      <td className="text-right font-mono">{Number(p.costPln).toFixed(2)} zł</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <Pagination total={perModelPage.total} page={perModelPage.page} pageSize={perModelPage.pageSize}
                        onPageChange={perModelPage.setPage} onPageSizeChange={perModelPage.setPageSize}/>
          </div>
        </div>

        <div className="card">
          <div className="card-head"><div className="card-title">Per krok workflow (K1–K12)</div></div>
          <div className="card-body">
            {data.perStep.length === 0 ? (
              <div className="text-ink-400 text-[13px]">Brak danych</div>
            ) : (
              <table className="table-hw">
                <thead>
                  <tr>
                    <th>Krok</th>
                    <th className="text-right">Wywołań</th>
                    <th className="text-right">Tokeny</th>
                    <th className="text-right">USD</th>
                    <th className="text-right">PLN</th>
                  </tr>
                </thead>
                <tbody>
                  {perStepPage.pagedItems.map((p, i) => (
                    <tr key={i}>
                      <td>
                        <span className="pill pill-earth">{String(p.code)}</span>
                      </td>
                      <td className="text-right font-mono">{String(p.liczbaWywolan)}</td>
                      <td className="text-right font-mono">
                        {Number(p.inputTokens) + Number(p.outputTokens)}
                      </td>
                      <td className="text-right font-mono text-green-500">${Number(p.costUsd).toFixed(4)}</td>
                      <td className="text-right font-mono">{Number(p.costPln).toFixed(2)} zł</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <Pagination total={perStepPage.total} page={perStepPage.page} pageSize={perStepPage.pageSize}
                        onPageChange={perStepPage.setPage} onPageSizeChange={perStepPage.setPageSize}/>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="card-title">Per study (top koszt)</div>
        </div>
        <div className="card-body !p-0">
          {data.perStudy.length === 0 ? (
            <div className="text-ink-400 text-[13px] p-4">Brak danych</div>
          ) : (
            <table className="table-hw">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Tytuł</th>
                  <th className="text-right">Wywołań</th>
                  <th className="text-right">Tokeny we</th>
                  <th className="text-right">Tokeny wy</th>
                  <th className="text-right">Łącznie</th>
                  <th className="text-right">USD</th>
                  <th className="text-right">PLN</th>
                </tr>
              </thead>
              <tbody>
                {perStudyPage.pagedItems.map(p => (
                  <tr key={String(p.studyId)}
                      onClick={() => window.location.href = '/studies/' + p.studyId}>
                    <td><span className="font-mono text-[11px]">{String(p.number)}</span></td>
                    <td className="truncate max-w-[360px]">{String(p.title)}</td>
                    <td className="text-right font-mono">{String(p.liczbaWywolan)}</td>
                    <td className="text-right font-mono">{String(p.inputTokens)}</td>
                    <td className="text-right font-mono">{String(p.outputTokens)}</td>
                    <td className="text-right font-mono font-semibold">{String(p.tokenyLacznie)}</td>
                    <td className="text-right font-mono text-green-500">${Number(p.costUsd).toFixed(4)}</td>
                    <td className="text-right font-mono font-semibold">{Number(p.costPln).toFixed(2)} zł</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {data.perStudy.length > 0 && (
            <div className="px-3 border-t border-ink-100">
              <Pagination total={perStudyPage.total} page={perStudyPage.page} pageSize={perStudyPage.pageSize}
                          onPageChange={perStudyPage.setPage} onPageSizeChange={perStudyPage.setPageSize}/>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, data, color }: { label: string; data: Record<string, number | string>; color: string }) {
  const tokenyIn = Number(data.inputTokens ?? 0);
  const tokenyOut = Number(data.outputTokens ?? 0);
  const costUsd = Number(data.costUsd ?? 0);
  const costPln = Number(data.costPln ?? 0);
  const calls = Number(data.liczbaWywolan ?? 0);

  return (
    <div className="kpi">
      <div className={'absolute inset-y-0 left-0 w-[3px] ' + color} />
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">${costUsd.toFixed(4)}</div>
      <div className="text-[14px] text-ink-700 mt-1 font-mono">{costPln.toFixed(2)} zł</div>
      <div className="grid grid-cols-2 gap-3.5 mt-2.5 text-[12px]">
        <div>
          <div className="text-ink-900 text-[15px] font-semibold">{calls}</div>
          <div className="text-ink-400 text-[10px] uppercase tracking-wider">wywołania</div>
        </div>
        <div>
          <div className="text-ink-900 text-[15px] font-semibold">{(tokenyIn + tokenyOut).toLocaleString('pl-PL')}</div>
          <div className="text-ink-400 text-[10px] uppercase tracking-wider">tokeny (we+wy)</div>
        </div>
      </div>
    </div>
  );
}
